import type { FileFormatClass } from './FileFormats';
import type { ProjectText } from '@easterngraphics/wcf/modules/eaiws/project';

import i18next from 'i18next';

import { notNull } from './Types';
import { parseFilename } from './string/parseFilename';

import { isNullOrEmpty, isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

export { parseFilename };

interface AsciiFilenameOptions {
    replaceCharacter?: string;
    extension?: string;
    extractExtension?: boolean;
    allowEmptyFilenames?: boolean;
    requireExtension?: boolean;
}

const MatchExtension: RegExp = new RegExp(/^([A-Za-z0-9]+)$/);
export function getAsciiFilename(value: string, options: AsciiFilenameOptions = {}): string {
    const { replaceCharacter = '_' } = options;

    let filename: string = value;
    let extension: string | undefined;

    if (options.extractExtension) {
        [filename, extension] = parseFilename(filename);
    }

    filename = convertUmlauts(filename).replace(/[^\w\-]/g, replaceCharacter);

    if (extension == null && options.extension != null) {
        extension = options.extension;
    }

    if (options.allowEmptyFilenames !== true && isNullOrEmpty(filename)) {
        throw new Error('invalid filename');
    }

    if (isNotNullOrEmpty(extension)) {
        if (!MatchExtension.test(extension)) {
            throw new Error('invalid extension');
        }

        return `${filename}.${extension}`;
    }

    if (options.requireExtension) {
        throw new Error('missing extension');
    }

    return filename;
}

const umlautReplacement = new Map([
    ['ä', 'ae'],
    ['Ä', 'Ae'],
    ['Ü', 'Ue'],
    ['ü', 'ue'],
    ['ö', 'oe'],
    ['Ö', 'Oe'],
    ['ß', 'ss'],
    ['ç', 'c'],
    ['ñ', 'n'],
    ['á', 'a'],
    ['à', 'a'],
    ['â', 'a'],
    ['Á', 'A'],
    ['À', 'A'],
    ['Â', 'A'],
    ['é', 'e'],
    ['è', 'e'],
    ['ê', 'e'],
    ['ë', 'e'],
    ['É', 'E'],
    ['È', 'E'],
    ['Ê', 'E'],
    ['Ë', 'E'],
    ['í', 'i'],
    ['ì', 'i'],
    ['î', 'i'],
    ['ï', 'i'],
    ['Í', 'I'],
    ['Ì', 'I'],
    ['Î', 'I'],
    ['Ï', 'I'],
    ['ó', 'o'],
    ['ò', 'o'],
    ['ô', 'o'],
    ['Ó', 'O'],
    ['Ò', 'O'],
    ['Ô', 'O'],
    ['ú', 'u'],
    ['ù', 'u'],
    ['û', 'u'],
    ['Ú', 'U'],
    ['Ù', 'U'],
    ['Û', 'U'],
]);

function convertUmlauts(input: string): string {
    let output: string = '';

    for (const char of input) {
        output += umlautReplacement.get(char) ?? char;
    }

    return output;
}

export function isDataUri(value: string | undefined | null): boolean {
    if (isNullOrEmpty(value)) {
        return false;
    }
    return /^\s*data:([a-z]+\/[a-z\+]+(;[a-z\-]+\=[a-z\-]+)?)?(;base64)?,[a-z0-9\!\$\&\'\,\(\)\*\+\,\;\=\-\.\_\~\:\@\/\?\%\s]*\s*$/i.test(value);
}

export function isUrl(value: string | undefined): value is string {
    return isNotNullOrEmpty(value) && value.startsWith('http');
}

export function getFileExtension(value: string | undefined): string {
    if (isNotNullOrEmpty(value)) {
        const splittedName: Array<string> = value.split('.');
        if (splittedName.length > 0) {
            return splittedName[splittedName.length - 1];
        }
    }
    return '';
}

export function getFilename(baseName: string, format: FileFormatClass): string {
    return getAsciiFilename(baseName, { extension: format.extension });
}

export function getProjectText(projectTexts: Array<ProjectText>, type: 'HeaderText' | 'FooterText', language: string): string | undefined {
    for (const projectText of projectTexts) {
        if (projectText.type === type && projectText.texts?.length) {
            const formattedTexts = projectText.texts;
            const formattedTextSpecific = formattedTexts.find((value) => {
                return value.language === language;
            });

            if (formattedTextSpecific) {
                return formattedTextSpecific.value;
            }

            const formattedTextFallback = formattedTexts.find((value) => {
                return isNullOrEmpty(value.language) || value.language === 'und';
            });

            return formattedTextFallback?.value;
        }
    }

    return;
}
export function textFromIML(value: string | undefined): string | undefined {
    if (value == null) {
        return;
    }

    try {
        const lines: Array<string> = [];
        const match = value.match(/@p{([^}]*)}/g);

        if (match == null) {
            return;
        }

        for (const part of match) {
            lines.push(part.replace(/@p{([^}]*)}/, '$1'));
        }

        return lines.join('\n');
    } catch {
        /* */
    }

    return;
}

/* Code copied from wbk */
type EaiwsJsonTag = 'indent' | 'ol' | 'ul' | 'li' | 'p' | 'b' | 'i' | 'u' | 'br';
type EaiwsJsonContent = Array<string | EaiwsJsonElement> | null;
type EaiwsJsonElement = [EaiwsJsonTag, null, EaiwsJsonContent] | [EaiwsJsonTag];
type EaiwsJsonText = ['iml', null, EaiwsJsonContent];

export function plainTextToEaiwsJson(text: string): EaiwsJsonText {
    const lines = text.trim().split('\n');

    const paragraphContent: EaiwsJsonContent = [];

    for (let i = 0; i < lines.length; ++i) {
        let line = lines[i];

        if (isNotNullOrEmpty(line)) {
            //EAIWS will remove consecutive spaces, replace every second space with a 'non breaking space' to avoid this
            line = line.replace(/ {2}/g, ' \u00A0');
            paragraphContent.push(line);
        }

        if (i < lines.length - 1) {
            paragraphContent.push(['br']);
        }
    }

    return ['iml', null, [['p', null, paragraphContent]]];
}

export function minMax(min: number | string | undefined, max: number | string | undefined): string {
    const values: [string | null, string | null] = [
        min != null ? `${i18next.t('min', { context: 'abbreviation for minimum' })}: ${min}` : null,
        max != null ? `${i18next.t('max', { context: 'abbreviation for maximum' })}: ${max}` : null,
    ];

    return values.filter(notNull).join(' - ');
}
